import React, { createContext, useContext, useState, useEffect } from 'react';
import { getPublicWebsiteMaintenanceStatus } from '../../api/system/systemStatus';
import { setWebsiteMaintenanceMode } from '../../admin-panel/api/systemStatusApi';

const WebsiteMaintenanceContext = createContext();
const POLL_MS = 12000;

export const useWebsiteMaintenance = () => {
  const context = useContext(WebsiteMaintenanceContext);
  if (!context) {
    throw new Error('useWebsiteMaintenance must be used within a WebsiteMaintenanceProvider');
  }
  return context;
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.canWrite] - Admin panel can persist toggles; the public site only reads status.
 */
export const WebsiteMaintenanceProvider = ({ children, adminFunctions, canWrite = Boolean(adminFunctions) }) => {
  const [isWebsiteMaintenance, setIsWebsiteMaintenance] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const status = await getPublicWebsiteMaintenanceStatus();
        if (cancelled) return;
        setIsWebsiteMaintenance(Boolean(status?.enabled));
      } catch (error) {
        console.error('Error loading website maintenance status:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const toggleWebsiteMaintenance = async () => {
    if (!canWrite) return false;
    try {
      const result = await setWebsiteMaintenanceMode({ enabled: !isWebsiteMaintenance });
      setIsWebsiteMaintenance(Boolean(result.enabled));
      return true;
    } catch (error) {
      console.error('Error toggling website maintenance mode:', error);
      return false;
    }
  };

  const value = {
    isWebsiteMaintenance,
    toggleWebsiteMaintenance,
    loading,
  };

  return (
    <WebsiteMaintenanceContext.Provider value={value}>
      {children}
    </WebsiteMaintenanceContext.Provider>
  );
};
